import React, { useMemo } from "react";
import { EntityFormProps } from "../../../common/entity/EntityInterfaces";
import * as yup from "yup";
import { ZontalCredentialsWriteModel } from "../../../api/Zontal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormHeader } from "../../../common/forms/FormHeader";
import styles from "../../../common/forms/forms.module.css";
import { InputFormField } from "../../../common/formfields/InputFormField";
import { PasswordFormField, PasswordFormFieldControlled } from "../../../common/formfields/PasswordFormField";
import { FormButtons } from "../../../common/forms/FormButtons";

export const ZontalCredentialsForm = ({
  event,
  fieldLabels,
  permissions,
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
}: EntityFormProps<"zontalCredentials">) => {
  const ZontalCredentialFormSchema = yup.object().shape({
    id: yup.number().nullable(),
    username: yup.string().required("Username is required").typeError("Username is required"),
    password: yup.string().required("Password is required").typeError("Password is required"),
    host: yup.string().required("Host is required").typeError("Host is required"),
    clientId: yup.string(),
    clientSecret: yup.string().when("clientId", {
      is: (val: string) => !!val,
      then: (schema) => schema.required("Client Secret is required").typeError("Client Secret is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Partial<ZontalCredentialsWriteModel>>({
    values: useMemo(() => initialValues, [initialValues]),
    resolver: yupResolver(ZontalCredentialFormSchema),
  });

  return (
    <>
      <FormHeader title={title} subtitle={subtitle} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">Details</legend>
        </fieldset>
        <InputFormField
          id="username"
          label={fieldLabels.username}
          errors={errors}
          register={register}
          autoFocus={true}
          required={true}
          placeholder="Enter user name..."
        />
        <PasswordFormField
          id="password"
          label={fieldLabels.password}
          errors={errors}
          register={register}
          autoFocus={true}
          required={true}
          placeholder="Enter password..."
        />
        <InputFormField
          id="host"
          label={fieldLabels.host}
          errors={errors}
          register={register}
          autoFocus={false}
          required={true}
          placeholder="Enter host..."
        />
        <InputFormField
          id="clientId"
          label={fieldLabels.clientId}
          errors={errors}
          register={register}
          autoFocus={false}
          required={false}
          placeholder="Enter client ID..."
        />
        <PasswordFormFieldControlled
          id="clientSecret"
          label={fieldLabels.clientSecret}
          errors={errors}
          control={control}
          autoFocus={false}
          required={false}
          placeholder="Enter client secret..."
        />

        <FormButtons
          onClose={onCancel}
          onSubmit={handleSubmit(async (entity) => await onSubmit(entity))}
          submitButtonLabel={submitButtonLabel || "Save changes"}
          disabled={isSubmitting}
          errors={errors}
          loading={loading}
          reset={["EDIT", "CLONE"].includes(event) ? () => reset(initialValues) : undefined}
          dirtyFields={["EDIT", "CLONE"].includes(event) ? Object.keys(dirtyFields).length : undefined}
        />
      </form>
    </>
  );
};
